import React, { createContext, useContext, useEffect, useState } from "react";
import fetchWithCredentials from "apiClient";
import Spinner from "components/ui/Spinner";
import { Teacher } from "types/Teacher";

type AuthContextType = {
  isAuthenticated: boolean;
  teacherId: number | null;
  teacherName: string | null;
  teacherCountry: string | null;
  lang: string | null;
  currentPlan: string;
  planLimit: number;
  hasPartner: boolean;
  partnerName: string | null;
  email: string | null;
  isSuperuser: boolean;
  isImpersonating: boolean;
  impersonatedTeacher: Teacher | null;
  subscriptionExpiresAt: string | null;
  logout: () => void;
  startImpersonation: (teacherUserId: number) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  getImpersonationStatus: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  teacherId: null,
  teacherName: null,
  teacherCountry: null,
  lang: "en",
  currentPlan: "Free",
  planLimit: 0,
  hasPartner: false,
  partnerName: null,
  email: null,
  isSuperuser: false,
  isImpersonating: false,
  impersonatedTeacher: null,
  subscriptionExpiresAt: null,
  logout: () => {},
  startImpersonation: async () => {},
  stopImpersonation: async () => {},
  getImpersonationStatus: async () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [teacherId, setTeacherId] = useState<number | null>(null);
  const [teacherName, setTeacherName] = useState<string | null>(null);
  const [teacherCountry, setTeacherCountry] = useState<string | null>(null);
  const [lang, setLang] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string>("Free");
  const [planLimit, setPlanLimit] = useState<number>(0);
  const [hasPartner, setHasPartner] = useState<boolean>(false);
  const [partnerName, setPartnerName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isSuperuser, setIsSuperuser] = useState<boolean>(false);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const [impersonatedTeacher, setImpersonatedTeacher] =
    useState<Teacher | null>(null);
  const [subscriptionExpiresAt, setSubscriptionExpiresAt] = useState<
    string | null
  >(null);
  const [loaded, setLoaded] = useState(false);

  const getImpersonationStatus = async () => {
    try {
      const status = await fetchWithCredentials("/api/impersonation-status/");
      setIsImpersonating(status.impersonating);
      if (status.impersonating && status.userId) {
        // Fetch teacher details
        const teachers = await fetchWithCredentials("/api/teachers/");
        const teacher = teachers.find(
          (t: Teacher) => t.userId === status.userId
        );
        setImpersonatedTeacher(teacher || null);
      } else {
        setImpersonatedTeacher(null);
      }
    } catch (err) {
      console.error("Failed to get impersonation status:", err);
    }
  };

  const startImpersonation = async (teacherUserId: number) => {
    const payload = { user_id: teacherUserId };

    try {
      await fetchWithCredentials("/api/impersonate/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      await getImpersonationStatus();
      // Refresh the page to update all contexts
      window.location.reload();
    } catch (err) {
      console.error("Failed to start impersonation:", err);
      throw err;
    }
  };

  const stopImpersonation = async () => {
    try {
      await fetchWithCredentials("/api/stop-impersonate/", {
        method: "POST",
      });
      await getImpersonationStatus();
      // Refresh the page to update all contexts
      window.location.reload();
    } catch (err) {
      console.error("Failed to stop impersonation:", err);
      throw err;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setLoaded(true);
      return;
    }

    async function fetchProfile() {
      try {
        const data = await fetchWithCredentials("/api/teacher/profile/");
        setTeacherId(data.id);
        setTeacherName(data.name);
        setTeacherCountry(data.country);
        setLang(data.lang || null);
        setCurrentPlan(data.plan || "Free");
        setPlanLimit(data.planLimit || 0);
        setHasPartner(data.hasPartner || false);
        setPartnerName(data.partnerName || null);
        setEmail(data.email);
        setSubscriptionExpiresAt(data.subscriptionExpiresAt || null);

        // Check if user is superuser by trying to access teachers list
        try {
          await fetchWithCredentials("/api/teachers/");
          setIsSuperuser(true);
        } catch {
          setIsSuperuser(false);
        }

        // Get impersonation status
        await getImpersonationStatus();
      } catch (err) {
        console.error("Auth profile fetch failed:", err);
        logout();
      } finally {
        setLoaded(true);
      }
    }

    fetchProfile();
  }, []);

  const logout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    setTeacherId(null);
    setTeacherName(null);
    setTeacherCountry(null);
    setCurrentPlan("Free");
    setPlanLimit(0);
    setHasPartner(false);
    setPartnerName(null);
    setEmail(null);
    setIsSuperuser(false);
    setIsImpersonating(false);
    setImpersonatedTeacher(null);
    setSubscriptionExpiresAt(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: Boolean(teacherId),
        teacherId,
        teacherName,
        teacherCountry,
        lang,
        currentPlan,
        planLimit,
        hasPartner,
        partnerName,
        email,
        isSuperuser,
        isImpersonating,
        impersonatedTeacher,
        subscriptionExpiresAt,
        logout,
        startImpersonation,
        stopImpersonation,
        getImpersonationStatus,
      }}
    >
      {loaded ? children : <Spinner className="h-screen" />}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
